import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import PremiumCelebration from '../components/PremiumCelebration/PremiumCelebration';

const UsageContext = createContext({
  usageCounts: {},
  incrementUsage: () => {},
  checkUsageLimit: () => true,
  resetUsage: () => {},
  showPricingModal: false,
  setShowPricingModal: () => {},
  currentUser: null,
  remainingUsage: {},
});

export const useUsage = () => {
  const context = useContext(UsageContext);
  if (!context) {
    throw new Error('useUsage must be used within a UsageProvider');
  }
  return context;
};

export const UsageProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [usageCounts, setUsageCounts] = useState({});
  const [remainingUsage, setRemainingUsage] = useState({});
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showPremiumCelebration, setShowPremiumCelebration] = useState(false);

  useEffect(() => {
    if (currentUser?.isPremium) {
      setShowPremiumCelebration(true);
      localStorage.setItem('premiumCelebrated', 'true');
    }
  }, [currentUser?.isPremium]);

  const incrementUsage = async (toolName) => {
    try {
      const response = await fetch('/api/increment-usage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await currentUser?.getIdToken()}`
        },
        body: JSON.stringify({ toolName })
      });

      const data = await response.json();
      
      if (data.requiresPremium) {
        setShowPricingModal(true);
        return false;
      }

      // Update local state with server response
      setUsageCounts(prev => ({
        ...prev,
        [toolName]: (prev[toolName] || 0) + 1
      }));

      setRemainingUsage(prev => ({
        ...prev,
        [toolName]: data.remainingUsage
      }));

      return true;
    } catch (error) {
      console.error('Failed to increment usage:', error);
      return false;
    }
  };

  const checkUsageLimit = async (toolName) => {
    if (currentUser?.isPremium) return true;
    
    try {
      const response = await fetch(`/api/check-usage?toolName=${toolName}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await currentUser?.getIdToken()}`
        }
      });

      const data = await response.json();
      
      if (data.requiresPremium) {
        setShowPricingModal(true);
        return false;
      }

      // Update remaining usage from server
      setRemainingUsage(prev => ({
        ...prev,
        [toolName]: data.remainingUsage
      }));

      return true;
    } catch (error) {
      console.error('Failed to check usage:', error);
      return false;
    }
  };

  const resetUsage = () => {
    setUsageCounts({});
    setRemainingUsage({});
  };

  const handleCelebrationComplete = () => {
    setShowPremiumCelebration(false);
  };

  const value = {
    usageCounts,
    remainingUsage,
    incrementUsage,
    checkUsageLimit,
    resetUsage,
    showPricingModal,
    setShowPricingModal,
    currentUser
  };

  return (
    <UsageContext.Provider value={value}>
      {children}
      {showPremiumCelebration && (
        <PremiumCelebration onComplete={handleCelebrationComplete} />
      )}
    </UsageContext.Provider>
  );
};
